/**
 * Custom field dimension keys, and the split that routes them to their own payload key.
 *
 * Lives in utils rather than beside the reports pages because the store's params builder needs it
 * too, and a store slice may not import from `pages`.
 */

const KEY_PATTERN = /^cf_(\d+)$/;

/**
 * Keyed on the field's primary key, matching the backend. Not `token`, which a user can rename,
 * and not the slot the answer occupies, which is reused once a field is deleted.
 */
export const customFieldKey = (customFieldId: number | string) =>
  `cf_${customFieldId}`;

export const isCustomFieldKey = (key: unknown) =>
  KEY_PATTERN.test(String(key ?? ''));

export const customFieldIdFromKey = (key: unknown) => {
  const match = String(key ?? '').match(KEY_PATTERN);
  return match ? Number(match[1]) : null;
};

/**
 * The reports filter bar speaks `in`/`ex`; `custom_filter` speaks the vocabulary the rest of the
 * product already uses for custom fields. The comparison operators are identical on both sides,
 * so only the set operators translate.
 */
const CONDITION_TO_CUSTOM_FILTER: Record<string, string> = {
  in: 'is',
  ex: 'is_not',
};

const SCALAR_OPERATORS = ['eq', 'gt', 'gte', 'lt', 'lte'];

/**
 * Splits applied filters into the two payload keys the backend reads.
 *
 * Custom fields travel under `custom_filter`, keyed by the custom field id, because that is the
 * shape core already parses for them everywhere else. Built-in dimensions are untouched and keep
 * flowing into `filter_by`, so no existing report changes shape.
 *
 * A comparison operator carries one bound, but the filter bar stores every value as a list, so the
 * single value is unwrapped — the backend reads a number there, not an array.
 */
export const splitCustomFilters = (
  filters: Record<string, Record<string, unknown>> = {}
) => {
  const reportFilters: Record<string, unknown> = {};
  const customFilter: Record<string, Record<string, unknown>> = {};

  Object.entries(filters || {}).forEach(([key, constraints]) => {
    const customFieldId = customFieldIdFromKey(key);
    if (customFieldId === null) {
      reportFilters[key] = constraints;
      return;
    }

    const translated: Record<string, unknown> = {};
    Object.entries(constraints || {}).forEach(([condition, value]) => {
      const operator = CONDITION_TO_CUSTOM_FILTER[condition] ?? condition;
      translated[operator] =
        SCALAR_OPERATORS.includes(operator) && Array.isArray(value)
          ? value[0]
          : value;
    });

    if (Object.keys(translated).length) {
      customFilter[String(customFieldId)] = translated;
    }
  });

  return { reportFilters, customFilter };
};
